// Shared model for the personal Palworld card collection binder (/collection).
// Kept isomorphic so the member page, the public share page and the server
// functions all agree on capacity, validation and profit maths.
import { z } from "zod";
import { BINDER_PAGES, BINDER_SLOTS_PER_PAGE } from "./binder-shared";

export const COLLECTION_CAPACITY = BINDER_SLOTS_PER_PAGE * BINDER_PAGES; // 108
export { BINDER_PAGES as COLLECTION_PAGES, BINDER_SLOTS_PER_PAGE as COLLECTION_SLOTS_PER_PAGE };

/** Condition a collector files the card under. */
export const CONDITIONS = ["raw", "sleeved", "psa", "bgs", "cgc"] as const;
export type Condition = (typeof CONDITIONS)[number];

export const CONDITION_LABEL: Record<Condition, string> = {
  raw: "Raw",
  sleeved: "Raw · sleeved",
  psa: "Graded · PSA",
  bgs: "Graded · BGS",
  cgc: "Graded · CGC",
};

const money = z.number().min(0).max(1_000_000).nullable().default(null);

export const addCardSchema = z.object({
  card_code: z.string().min(2).max(24),
  quantity: z.number().int().min(1).max(999).default(1),
  condition: z.enum(CONDITIONS).default("raw"),
  buy_price: money,
  sell_price: money,
  notes: z.string().max(240).default(""),
  slot: z.number().int().min(0).max(COLLECTION_CAPACITY - 1).nullable().default(null),
});

export const updateCardSchema = z.object({
  id: z.string().uuid(),
  quantity: z.number().int().min(1).max(999).optional(),
  condition: z.enum(CONDITIONS).optional(),
  buy_price: z.number().min(0).max(1_000_000).nullable().optional(),
  sell_price: z.number().min(0).max(1_000_000).nullable().optional(),
  notes: z.string().max(240).optional(),
  /** Realized sale: what the copy actually sold for, per copy. */
  sold_price: z.number().min(0).max(1_000_000).nullable().optional(),
  /** ISO timestamp the sale completed, or null to reopen the card as unsold. */
  sold_at: z.string().datetime().nullable().optional(),
});


export const moveCardSchema = z.object({
  id: z.string().uuid(),
  slot: z.number().int().min(0).max(COLLECTION_CAPACITY - 1),
});

export const removeCardSchema = z.object({ id: z.string().uuid() });
export const shareLabelSchema = z.object({ label: z.string().max(60).default("Collection share link") });
export const shareTokenSchema = z.object({
  token: z.string().min(24).max(120).regex(/^[A-Za-z0-9_-]+$/),
});
export const revokeShareSchema = z.object({ id: z.string().uuid() });

/** One row of a member's tracked collection. */
export interface CollectionRow {
  id: string;
  card_code: string;
  slot: number | null;
  quantity: number;
  condition: string;
  buy_price: number | null;
  sell_price: number | null;
  /** What the copy actually sold for (realized), per copy. */
  sold_price: number | null;
  /** When the sale completed. Null means the card is still held. */
  sold_at: string | null;
  notes: string;
  created_at: string;
}

export interface CollectionTotals {
  cards: number;
  copies: number;
  invested: number;
  valued: number;
  profit: number;
  roi: number | null;
  priced: number;
  /** Cards marked as really sold. */
  soldCards: number;
  /** Cash actually taken on sold cards. */
  realized: number;
  /** Cost basis of the sold cards. */
  realizedCost: number;
  /** Profit already banked from real sales. */
  realizedProfit: number;
  /** Paper profit on cards still held. */
  unrealizedProfit: number;
}

/** Price a row is counted at: the real sale price once it has sold. */
export function effectiveSell(r: CollectionRow): number | null {
  if (r.sold_at) return r.sold_price ?? r.sell_price;
  return r.sell_price;
}

export function isSold(r: CollectionRow): boolean {
  return Boolean(r.sold_at);
}

/**
 * Portfolio maths. Buy price is what the collector paid per copy; sell price is
 * the asking price, also per copy. Once a card is marked sold its real sale
 * price takes over and its profit moves from paper (unrealized) into banked
 * (realized). Profit is only counted for rows that carry both numbers, so blank
 * fields never invent a gain or a loss.
 */
export function totalsOf(rows: CollectionRow[]): CollectionTotals {
  let invested = 0;
  let valued = 0;
  let copies = 0;
  let priced = 0;
  let soldCards = 0;
  let realized = 0;
  let realizedCost = 0;
  let realizedProfit = 0;
  let unrealizedProfit = 0;
  for (const r of rows) {
    const qty = Math.max(1, r.quantity);
    const sell = effectiveSell(r);
    copies += qty;
    if (r.buy_price !== null) invested += r.buy_price * qty;
    if (sell !== null) valued += sell * qty;
    if (r.buy_price !== null && sell !== null) priced += qty;
    const rowProfit = r.buy_price !== null && sell !== null ? (sell - r.buy_price) * qty : 0;
    if (isSold(r)) {
      soldCards++;
      if (sell !== null) realized += sell * qty;
      if (r.buy_price !== null) realizedCost += r.buy_price * qty;
      realizedProfit += rowProfit;
    } else {
      unrealizedProfit += rowProfit;
    }
  }
  const profit = valued - invested;
  return {
    cards: rows.length,
    copies,
    invested,
    valued,
    profit,
    roi: invested > 0 ? profit / invested : null,
    priced,
    soldCards,
    realized,
    realizedCost,
    realizedProfit,
    unrealizedProfit,
  };
}


export function usd(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}
